import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
interface CarouselImage {
  src: string;
  alt: string;
}
interface CategoryCarouselProps {
  title: string;
  images: CarouselImage[];
  index: number;
}
export function CategoryCarousel({
  title,
  images,
  index
}: CategoryCarouselProps) {
  const [active, setActive] = useState(0);
  const [offset, setOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);
  const total = images.length;
  const recalc = useCallback(() => {
    const container = containerRef.current;
    const slide = slideRef.current;
    if (!container || !slide) return;
    const containerWidth = container.offsetWidth;
    // slide.offsetWidth is the inner width; include the horizontal margin (gap)
    const style = window.getComputedStyle(slide);
    const marginLeft = parseFloat(style.marginLeft) || 0;
    const marginRight = parseFloat(style.marginRight) || 0;
    const slideWidth = slide.offsetWidth;
    const slideTotal = slideWidth + marginLeft + marginRight;
    // Center the active slide within the container
    const centerOffset = containerWidth / 2 - slideWidth / 2;
    setOffset(centerOffset - active * slideTotal);
  }, [active]);
  useEffect(() => {
    recalc();
    window.addEventListener('resize', recalc);
    return () => window.removeEventListener('resize', recalc);
  }, [recalc]);
  const goPrev = () => setActive(p => Math.max(0, p - 1));
  const goNext = () => setActive(p => Math.min(total - 1, p + 1));
  const handleDragEnd = (_: unknown, info: {
    offset: {
      x: number;
    };
    velocity: {
      x: number;
    };
  }) => {
    const threshold = 60;
    if (info.offset.x < -threshold || info.velocity.x < -400) {
      goNext();
    } else if (info.offset.x > threshold || info.velocity.x > 400) {
      goPrev();
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.6,
    delay: index * 0.05
  }} className="mb-20 last:mb-0" data-id="element-33">
      {/* Category header */}
      <div className="mb-6 px-1" data-id="element-34">
        <h3 className="text-2xl md:text-3xl font-serif font-bold text-espresso" data-id="element-35">
          {title}
        </h3>
        <div className="w-12 h-0.5 bg-gold mt-2" data-id="element-36"></div>
      </div>

      {/* Carousel viewport */}
      <div ref={containerRef} className="relative overflow-hidden" data-id="element-37">
        {/* Overlay navigation arrows — always visible on desktop & mobile */}
        <button onClick={goPrev} disabled={active === 0} aria-label={`Imagen anterior de ${title}`} className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-bone/90 backdrop-blur-sm border border-espresso/10 shadow-md flex items-center justify-center text-espresso hover:bg-gold hover:border-gold hover:text-espresso disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300" data-id="element-38">
          <ChevronLeft size={22} data-id="element-39" />
        </button>
        <button onClick={goNext} disabled={active === total - 1} aria-label={`Siguiente imagen de ${title}`} className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-bone/90 backdrop-blur-sm border border-espresso/10 shadow-md flex items-center justify-center text-espresso hover:bg-gold hover:border-gold hover:text-espresso disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300" data-id="element-40">
          <ChevronRight size={22} data-id="element-41" />
        </button>
        <motion.div className="flex cursor-grab active:cursor-grabbing" drag="x" dragConstraints={{
        left: 0,
        right: 0
      }} dragElastic={0.12} onDragEnd={handleDragEnd} animate={{
        x: offset
      }} transition={{
        type: 'spring',
        stiffness: 260,
        damping: 32
      }} data-id="element-42">
          {images.map((img, i) => {
          const isActive = i === active;
          return <div key={i} ref={i === 0 ? slideRef : undefined} className="flex-[0_0_82%] sm:flex-[0_0_70%] md:flex-[0_0_58%] lg:flex-[0_0_52%] mx-2 md:mx-3" data-id="element-43">
                <motion.button onClick={() => !isActive && setActive(i)} animate={{
              scale: isActive ? 1 : 0.9,
              opacity: isActive ? 1 : 0.55
            }} transition={{
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1]
            }} className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl shadow-lg group block" tabIndex={isActive ? 0 : -1} aria-label={img.alt} data-id="element-44">
                  <img src={img.src} alt={img.alt} draggable={false} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 pointer-events-none select-none" data-id="element-45" />
                  {/* Subtle dark gradient for legibility */}
                  <div className="absolute inset-0 bg-gradient-to-t from-espresso/60 via-transparent to-transparent" data-id="element-46"></div>
                  <div className="absolute bottom-0 left-0 p-5 md:p-6" data-id="element-47">
                    <span className="text-bone font-serif text-lg md:text-xl" data-id="element-48">
                      {img.alt}
                    </span>
                  </div>
                </motion.button>
              </div>;
        })}
        </motion.div>
      </div>

      {/* Dots indicator */}
      <div className="flex items-center justify-center gap-2 mt-6" data-id="element-49">
        {images.map((_, i) => <button key={i} onClick={() => setActive(i)} aria-label={`Ir a la imagen ${i + 1} de ${title}`} className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? 'w-8 bg-gold' : 'w-1.5 bg-espresso/25 hover:bg-espresso/40'}`} data-id="element-50" />)}
      </div>
    </motion.div>;
}